/**
 * Date utilities for UTC ↔ local timezone conversion.
 * All API dates are stored/transmitted as UTC ISO strings.
 * All display dates are formatted in the user's configured timezone.
 */

import { useUIStore } from '@/core/stores/ui.store';

/** Parse an API date string as UTC, return a Date object in local timezone */
export function parseApiDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;

  // If the string has no time component, treat it as midnight UTC
  const iso = dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00Z`;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/** Convert a local Date to a UTC ISO string for API transmission */
export function toApiDate(date: Date | null | undefined): string | null {
  if (!date) return null;
  return date.toISOString();
}

/** Convert a local Date to UTC date-only string (YYYY-MM-DD) */
export function toApiDateOnly(date: Date | null | undefined): string | null {
  if (!date) return null;
  return date.toISOString().split('T')[0];
}

/** Format just the time portion of a date, using the user's locale/timezone/12-24h preferences. */
export function formatDisplayTime(date: Date | string | null | undefined): string {
  if (!date) return '';

  const d = typeof date === 'string' ? parseApiDate(date) : date;
  if (!d) return '';

  const { timeFormat, timezone } = useUIStore.getState();

  return new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: timeFormat.includes('a'),
  }).format(d);
}

/** Format a date for display using user's locale/timezone preferences */
export function formatDisplayDate(
  date: Date | string | null | undefined,
  options?: { showTime?: boolean },
): string {
  if (!date) return '';

  const d = typeof date === 'string' ? parseApiDate(date) : date;
  if (!d) return '';

  const { timezone } = useUIStore.getState();

  const datePart = new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);

  if (!options?.showTime) return datePart;

  return `${datePart} ${formatDisplayTime(d)}`;
}

/** Check if a date is today in the user's timezone */
export function isTodayInTz(date: Date | string | null | undefined): boolean {
  if (!date) return false;
  const d = typeof date === 'string' ? parseApiDate(date) : date;
  if (!d) return false;

  const { timezone } = useUIStore.getState();
  const now = new Date();

  const dateStr = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);

  const nowStr = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);

  return dateStr === nowStr;
}

/**
 * A date's calendar day, as `YYYY-MM-DD`, in the user's configured timezone (`ui.store`) — not
 * the browser's. Use for calendar-day comparisons/grouping (e.g. the activity feed's "Hoy"/"Ayer"
 * day dividers), where the boundary must match what the user sees, not where their device thinks it is.
 */
export function dateOnlyInTz(date: Date | string): string {
  const d = typeof date === 'string' ? parseApiDate(date) : date;
  const { timezone } = useUIStore.getState();
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d ?? new Date());
}

/**
 * Today's date, as `YYYY-MM-DD`, in the user's configured timezone (`ui.store`) — not the
 * browser's. Use for API params that expect a literal calendar-day boundary (e.g. board activity
 * summary `From`/`To`), where "today" must match what the user sees, not where their device thinks it is.
 */
export function todayDateOnlyInTz(): string {
  return dateOnlyInTz(new Date());
}

/**
 * Get the UTC timestamp (ms since epoch) from a date string.
 * Returns null for null/undefined/invalid.
 */
export function getDateTimestamp(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const d = parseApiDate(dateStr);
  return d ? d.getTime() : null;
}

/**
 * Compare two date strings robustly by their UTC timestamp value.
 * Returns true if they represent the same moment in time.
 * Handles mixed formats: date-only, datetime, with/without ms, etc.
 */
export function areDatesEqual(
  date1: string | null | undefined,
  date2: string | null | undefined,
): boolean {
  const ts1 = getDateTimestamp(date1);
  const ts2 = getDateTimestamp(date2);

  // Both null/undefined = equal
  if (ts1 === null && ts2 === null) return true;

  // One null, one not = not equal
  if (ts1 === null || ts2 === null) return false;

  // Compare by actual UTC timestamp
  return ts1 === ts2;
}
